import { ref, onMounted, onBeforeUnmount } from 'vue';

// iOS/iPadOS Safari only lets an <audio> element be started from code (e.g. a timer tick)
// after that specific element has been played once from inside a real user gesture.
// Desktop browsers just need any prior interaction with the page, which is why sounds worked
// there but stayed silent on iPads. This plays each element (briefly, then pauses) on the first
// tap/click/keypress so later programmatic play() calls are allowed.
//
// getElements: function returning the current <audio> elements (refs resolve after mount).
//
// Returns:
//   playSound(el)  - play an element; if the browser blocks it, sets soundBlocked and re-arms the unlock
//   soundBlocked   - ref(true) while a sound was blocked, so the UI can show a "tap to enable sound" button
//   unlock()       - attempt the unlock (call from a click handler)
//   showSilentHint / dismissSilentHint - iPads/iPhones can't detect or bypass silent mode, which mutes these sounds,
//                    so the UI shows a one-time dismissible hint there (dismissal is remembered in localStorage)
const GESTURE_EVENTS = ['touchend', 'click', 'keydown'];
const HINT_KEY = 'silentModeHintDismissed';

// iPadOS Safari reports a Mac user agent, so also check for touch support
const isAppleTouchDevice = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Mac/.test(navigator.platform) && navigator.maxTouchPoints > 1);

function hintDismissed() {
  try { return localStorage.getItem(HINT_KEY) === '1'; } catch (e) { return false; }
}

export function useAudioUnlock(getElements) {
  const unlocked = new WeakSet();
  const soundBlocked = ref(false);
  const showSilentHint = ref(isAppleTouchDevice() && !hintDismissed());
  let listening = false;

  async function unlockElement(el, silently) {
    if (unlocked.has(el)) return true;
    // Don't interrupt a sound that is already playing (it is clearly allowed to play)
    if (!el.paused) { unlocked.add(el); return true; }
    // play() must be called synchronously inside the gesture handler. It is muted so the tap
    // isn't audible; if that ever fails to unlock, the "tap to enable" fallback plays it audibly.
    el.muted = silently;
    try {
      await el.play();
      el.pause();
      el.currentTime = 0;
      unlocked.add(el);
      return true;
    } catch (e) {
      // NotAllowedError = not a valid gesture yet, try again on the next one.
      // Anything else (e.g. missing file) can't be fixed by unlocking, so stop retrying it.
      if (e?.name !== 'NotAllowedError') { unlocked.add(el); return true; }
      return false;
    } finally {
      el.muted = false;
    }
  }

  async function unlock() {
    const els = (getElements() || []).filter(Boolean);
    if (els.length === 0) return;
    // A file that never loads (e.g. missing tournament-start.mp3) must not leave this pending forever
    const withTimeout = (p) => Promise.race([p, new Promise(resolve => setTimeout(() => resolve(false), 3000))]);
    const results = await Promise.all(els.map(el => withTimeout(unlockElement(el, !soundBlocked.value))));
    if (results.every(Boolean)) {
      soundBlocked.value = false;
      removeListeners();
    }
  }

  function dismissSilentHint() {
    showSilentHint.value = false;
    try { localStorage.setItem(HINT_KEY, '1'); } catch (e) { /* hint will just show again next visit */ }
  }

  function addListeners() {
    if (listening) return;
    listening = true;
    GESTURE_EVENTS.forEach(evt => document.addEventListener(evt, unlock, true));
  }

  function removeListeners() {
    if (!listening) return;
    listening = false;
    GESTURE_EVENTS.forEach(evt => document.removeEventListener(evt, unlock, true));
  }

  function playSound(el) {
    if (!el) return;
    el.play().catch(e => {
      if (e?.name === 'NotAllowedError') {
        // Browser refused: forget the unlock, tell the UI, and retry on the next tap
        unlocked.delete(el);
        soundBlocked.value = true;
        addListeners();
      } else {
        console.error('Error playing sound:', e);
      }
    });
  }

  onMounted(addListeners);
  onBeforeUnmount(removeListeners);

  return { unlock, playSound, soundBlocked, showSilentHint, dismissSilentHint };
}
